import React, { useState } from 'react';
import { useSpring, animated } from '@react-spring/web';
import { useInView } from 'react-intersection-observer';
import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { handleSignupRedirect } from '../../../utils/locationUtils';
import './ContentSections.css';
import brandimage from '../../../assets/images/brand-image.jpg';
import creatorimage from '../../../assets/images/creator-group-home.jpg';
import { creatorOrange, brandGreen } from '../../../constants/colors.js';

const ContentSections = () => {
  const handleLoginClick = () => {
    handleSignupRedirect();
  };

  return (
    <div className="content-sections">
      <Section
        title="FOR BRANDS"
        heading="WIN ATTENTION WITH EVERYDAY CREATIVITY"
        description="Fuelled by media, our solution is more than just content. We deliver diverse, insight-driven creative which acts as the targeting layer itself. Our channel agnostic approach is built to scale across platforms and audiences."
        buttonText="GET ACCESS"
        imageSrc={brandimage}
        headingColor="#00B1B5"
        hoverColor={brandGreen}
        imagePosition="right"
        navigateTo="/lets-talk"
        useLoginClick={false}
      />
      <Section
        title="FOR CREATORS"
        heading="UNLOCK YOUR CREATIVITY"
        description="We provide opportunities to work with the biggest brands and a new way to monetise your content. Whether you are an established creator or just starting out, EDC is the next step on your creator journey."
        buttonText="GET ACCESS"
        imageSrc={creatorimage}
        headingColor={'#FF872F'}
        hoverColor={creatorOrange}
        imagePosition="left"
        navigateTo="/lets-talk"
        useLoginClick={true}
        onLoginClick={handleLoginClick}
      />
    </div>
  );
};

const Section = ({
  title,
  heading,
  description,
  buttonText,
  imageSrc,
  headingColor,
  hoverColor,
  imagePosition,
  navigateTo,
  useLoginClick = false,
  onLoginClick,
}) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const bounceAnimation = useSpring({
    transform: inView ? 'scale(1)' : 'scale(0.8)',
    opacity: inView ? 1 : 0,
    config: { tension: 170, friction: 12, mass: 1 },
  });

  const navigate = useNavigate();
  const isMobile = useMediaQuery({ query: '(max-width: 768px)' });

  const handleButtonClick = () => {
    if (useLoginClick && onLoginClick) {
      onLoginClick();
    } else if (useLoginClick) {
      handleSignupRedirect();
    } else {
      navigate(navigateTo);
    }
  };

  // Function to render description as paragraphs
  const renderDescriptionAsParagraphs = text => {
    const sentences = text.split('. ');
    return sentences.map((sentence, index) => (
      <p key={index} className="section-description-paragraph">
        {sentence}
        {index < sentences.length - 1 ? '.' : ''}
      </p>
    ));
  };

  return (
    <div className="section-container">
      <animated.div
        ref={ref}
        style={bounceAnimation}
        className={`section ${
          imagePosition === 'left' ? 'image-left' : 'image-right'
        }`}
      >
        {isMobile ? (
          // Mobile layout - Image first, then content
          <>
            <div className="section-image-container">
              <div
                className={`section-image-outline ${
                  imagePosition === 'left' ? 'left-outline' : 'right-outline'
                }`}
              >
                <div
                  className={`${
                    imagePosition === 'left'
                      ? 'section-image-left'
                      : 'section-image-right'
                  }`}
                >
                  <img src={imageSrc} alt={title} />
                </div>
              </div>
            </div>
            <div className="section-content">
              <h3 className="section-title">{title}</h3>
              <h2 className="section-heading" style={{ color: headingColor }}>
                {heading}
              </h2>
              <div className="section-description">
                {renderDescriptionAsParagraphs(description)}
              </div>
              <button
                className="learn-more-button"
                style={{
                  '--hover-color': hoverColor,
                }}
                onClick={handleButtonClick}
              >
                {buttonText}
              </button>
            </div>
          </>
        ) : (
          // Desktop layout - Original order
          <>
            {imagePosition === 'left' && (
              <div className="section-image-container">
                <div className="section-image-outline left-outline">
                  <div className="section-image-left">
                    <img src={imageSrc} alt={title} />
                  </div>
                </div>
              </div>
            )}
            <div className="section-content">
              <h3 className="section-title">{title}</h3>
              <h2 className="section-heading" style={{ color: headingColor }}>
                {heading}
              </h2>
              <div className="section-description">
                {renderDescriptionAsParagraphs(description)}
              </div>
              <button
                className="learn-more-button"
                style={{
                  '--hover-color': hoverColor,
                }}
                onClick={handleButtonClick}
              >
                {buttonText}
              </button>
            </div>
            {imagePosition === 'right' && (
              <div className="section-image-container">
                <div className="section-image-outline right-outline">
                  <div className="section-image-right">
                    <img src={imageSrc} alt={title} />
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </animated.div>
    </div>
  );
};

export default ContentSections;
